import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, Loader2, Send, Clock, MapPin, CalendarPlus, Eye, GraduationCap } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Applicant } from "@/types/applicant";
import EmailPreviewStep from "./EmailPreviewStep";
import { generateInterviewPreviewHtml } from "@/lib/emailPreviewTemplates";

const NOTE_TEMPLATES: Record<string, { label: string; text: string }> = {
  azubi: {
    label: "Auszubildende/r (Azubi)",
    text:
      "Hinweis für Auszubildende:\n\n" +
      "• Bitte bringen Sie Ihren Personalausweis sowie die letzten beiden Schulzeugnisse (Kopie) mit.\n" +
      "• Falls Sie noch minderjährig sind, möchten wir Sie bitten, in Begleitung eines Elternteils oder Erziehungsberechtigten zu erscheinen.\n" +
      "• Planen Sie für das Gespräch ca. 45–60 Minuten ein. Sie erhalten einen Überblick über den Ausbildungsablauf, die Berufsschule sowie die praktische Tätigkeit im Betrieb.\n" +
      "• Im Anschluss zeigen wir Ihnen gerne unsere Räumlichkeiten und stellen Ihnen das Team vor.\n" +
      "• Vor Ausbildungsbeginn benötigen wir zudem eine ärztliche Bescheinigung nach dem Jugendarbeitsschutzgesetz (§ 32 JArbSchG) – die Untersuchung ist für Sie kostenfrei.\n\n" +
      "Bei Fragen rund um die Ausbildung können Sie sich jederzeit gerne vorab bei uns melden.",
  },
  praktikum: {
    label: "Praktikum / Schnuppertag",
    text:
      "Hinweis zum Praktikum / Schnuppertag:\n\n" +
      "• Bitte erscheinen Sie in praktischer, wettertauglicher Arbeitskleidung sowie festem, geschlossenem Schuhwerk.\n" +
      "• Sicherheitsschuhe und persönliche Schutzausrüstung werden Ihnen bei Bedarf vor Ort gestellt.\n" +
      "• Bringen Sie bitte Verpflegung für die Pausen sowie eine gültige Krankenversicherungskarte mit.\n" +
      "• Bei Minderjährigen ist eine schriftliche Einverständniserklärung der Erziehungsberechtigten erforderlich.",
  },
  fachkraft: {
    label: "Fachkraft / Berufserfahrene",
    text:
      "Hinweis zum Vorstellungsgespräch:\n\n" +
      "• Bitte bringen Sie Ihren Lebenslauf, relevante Zeugnisse sowie Nachweise über Qualifikationen und Zertifikate mit.\n" +
      "• Falls vorhanden, bringen Sie bitte Ihren Führerschein sowie ggf. weitere berufsspezifische Nachweise mit.\n" +
      "• Planen Sie für das Gespräch ca. 60 Minuten ein.",
  },
  arbeitsbereich: {
    label: "Unseren Arbeitsbereich vorstellen (Handwerk)",
    text:
      "Über unseren Arbeitsbereich:\n\n" +
      "• Wir sind ein Handwerksbetrieb und Ihre Ausbildung findet überwiegend direkt auf unseren Baustellen sowie in unserer Werkstatt statt.\n" +
      "• Sie lernen den Umgang mit modernen Werkzeugen, Maschinen und Materialien kennen und sind von Beginn an aktiv ins Tagesgeschäft eingebunden.\n" +
      "• Die Tätigkeit ist abwechslungsreich, körperlich aktiv und findet je nach Auftrag im Innen- wie im Außenbereich statt – wettertaugliche Kleidung ist daher wichtig.\n" +
      "• Teamarbeit, Zuverlässigkeit und handwerkliches Interesse sind uns besonders wichtig – fachliche Kenntnisse vermitteln wir Ihnen Schritt für Schritt.\n" +
      "• Gerne zeigen wir Ihnen beim Gespräch unsere Räumlichkeiten und geben Ihnen einen ersten Einblick in unsere Projekte und das Team.",
  },
};

interface InterviewInvitationDialogProps {
  applicant: Applicant;
  onSuccess?: () => void;
}

const InterviewInvitationDialog = ({
  applicant,
  onSuccess,
}: InterviewInvitationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyAddress, setCompanyAddress] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [companyName, setCompanyName] = useState("Unser Unternehmen");
  const [companyEmail, setCompanyEmail] = useState<string | undefined>();
  const [companyInfo, setCompanyInfo] = useState<{ logoUrl?: string; street?: string; houseNumber?: string; postalCode?: string; city?: string; phone?: string }>({});

  useEffect(() => {
    if (open) loadCompanyData();
  }, [open]);

  const loadCompanyData = async () => {
    try {
      const { data } = await supabase
        .from("company_settings")
        .select("name, street, house_number, postal_code, city, email, logo_url, phone")
        .limit(1)
        .maybeSingle();

      if (data) {
        if (data.name) setCompanyName(data.name);
        if (data.email) setCompanyEmail(data.email);
        setCompanyInfo({
          logoUrl: data.logo_url || undefined,
          street: data.street || undefined,
          houseNumber: data.house_number || undefined,
          postalCode: data.postal_code || undefined,
          city: data.city || undefined,
          phone: data.phone || undefined,
        });
        const addressParts = [
          data.name,
          [data.street, data.house_number].filter(Boolean).join(" "),
          [data.postal_code, data.city].filter(Boolean).join(" ")
        ].filter(Boolean);
        const fullAddress = addressParts.join(", ");
        setCompanyAddress(fullAddress);
        if (!location) setLocation(fullAddress);
      }
    } catch (error) {
      console.error("Error loading company data:", error);
    }
  };

  const handleShowPreview = () => {
    if (!date) {
      toast.error("Bitte wählen Sie ein Datum aus");
      return;
    }
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!date) return;
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("send-interview-invitation", {
        body: {
          applicantId: applicant.id,
          proposedDate: format(date, "yyyy-MM-dd"),
          proposedTime: time,
          location,
          notes,
        },
      });
      if (error) throw error;
      toast.success("Einladung erfolgreich gesendet");
      setOpen(false);
      setShowPreview(false);
      onSuccess?.();
      setDate(undefined);
      setTime("10:00");
      setLocation("");
      setNotes("");
    } catch (error: any) {
      console.error("Error sending invitation:", error);
      toast.error("Fehler beim Senden der Einladung");
    } finally {
      setLoading(false);
    }
  };

  const applicantName = `${applicant.first_name} ${applicant.last_name}`;
  const formattedDate = date ? date.toLocaleDateString("de-DE", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "";
  const previewHtml = generateInterviewPreviewHtml(applicantName, companyName, formattedDate, time, location, notes || undefined, companyEmail, companyInfo);
  const previewSubject = `Einladung zum Vorstellungsgespräch - ${companyName}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setShowPreview(false); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <CalendarPlus className="h-4 w-4 mr-2" />
          Zum Gespräch einladen
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            Einladung zum Vorstellungsgespräch
          </DialogTitle>
        </DialogHeader>

        {showPreview ? (
          <EmailPreviewStep
            html={previewHtml}
            subject={previewSubject}
            recipientEmail={applicant.email}
            onBack={() => setShowPreview(false)}
            onConfirmSend={handleSubmit}
            loading={loading}
            sendButtonLabel="Einladung senden"
            loadingIcon={<Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          />
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm"><strong>Bewerber:</strong> {applicantName}</p>
                <p className="text-sm text-muted-foreground">{applicant.email}</p>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Datum</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("justify-start text-left font-normal", !date && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP", { locale: de }) : "Datum auswählen"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} locale={de} disabled={(d) => d < new Date()} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid gap-2">
                  <Label className="flex items-center gap-2"><Clock className="h-4 w-4" />Uhrzeit</Label>
                  <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                </div>

                <div className="grid gap-2">
                  <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" />Ort / Treffpunkt</Label>
                  <Input placeholder="z.B. Firmenadresse oder 'Online-Meeting'" value={location} onChange={(e) => setLocation(e.target.value)} />
                  {companyAddress && (
                    <Button type="button" variant="ghost" size="sm" className="w-fit text-xs text-muted-foreground hover:text-foreground" onClick={() => setLocation(companyAddress)}>
                      <MapPin className="h-3 w-3 mr-1" />Firmenadresse verwenden
                    </Button>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label className="flex items-center gap-2"><GraduationCap className="h-4 w-4" />Textvorlagen einfügen (optional, mehrere möglich)</Label>
                  <div className="rounded-md border p-3 space-y-2 bg-muted/30">
                    {Object.entries(NOTE_TEMPLATES).map(([k, v]) => (
                      <label key={k} className="flex items-start gap-2 cursor-pointer text-sm">
                        <Checkbox
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNotes((prev) => (prev?.trim() ? `${prev.trim()}\n\n${v.text}` : v.text));
                              toast.success(`Vorlage „${v.label}" eingefügt`);
                            }
                          }}
                        />
                        <span className="leading-tight">{v.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Zusätzliche Hinweise (optional)</Label>
                  <Textarea placeholder="z.B. Bitte bringen Sie folgende Unterlagen mit..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} />
                </div>
              </div>
            </div>


            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={handleShowPreview} variant="outline" disabled={!date}>
                <Eye className="h-4 w-4 mr-2" />
                Vorschau
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InterviewInvitationDialog;
